import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button, ButtonGroup, Col, FormGroup, Input, Label, Row, Table, UncontrolledTooltip } from 'reactstrap';
import { CorePasswordChangeRequest, CorePasswordChangeResult, CorePasswordChangeUser } from '../../models/CorePasswordChangeModels';
import { CoreAppToken } from '../../models/CoreAppToken';
import { ListPasswordAppTokens } from '../../store/PasswordAppTokenAction';
import { ListPasswordProfiles } from '../../store/PasswordProfileAction';
import { UpdateUserPasswords } from '../../store/PasswordChangeAction';
import { RootState } from '../../store/store';
import StatusCode from '../../util/StatusCode';
import Copy from '../misc/Copy';
import CsvDownloadButton from '../misc/CsvDownloadButton';
import ModalComponent from '../misc/ModalComponent';
import { AppTokenType } from '../../models/CoreModels';

enum PasswordSourceMode {
    PASSWORD = 'PASSWORD',
    PROFILE = 'PROFILE'
}

enum EnvironmentType {
    GOOGLE = 'Google',
    ENTRA_ID = 'EntraID',
}

interface PasswordChangeModalProps {
    isOpen: boolean
    selectedUsers: CorePasswordChangeUser[]
    selectedEnvironment?: string
    onClose: () => void
    onSuccess: () => void
}

const SELECTED_USERS_PREVIEW_LIMIT = 6;

interface SelectedUsersPreviewProps {
    selectedUsers: CorePasswordChangeUser[];
}

const SelectedUsersPreview: React.FC<SelectedUsersPreviewProps> = ({
    selectedUsers
}) => {
    const { t } = useTranslation(['common']);
    const [showAllSelectedUsers, setShowAllSelectedUsers] = useState<boolean>(false);

    if (!selectedUsers.length) {
        return null;
    }

    const selectedUsersPreview = showAllSelectedUsers
        ? selectedUsers
        : selectedUsers.slice(0, SELECTED_USERS_PREVIEW_LIMIT);
    const remainingSelectedUsers = selectedUsers.length - selectedUsersPreview.length;

    return (
        <div className='pe-2'>
            <Table striped>
                <thead>
                    <tr>
                        <th>{t('genericTableHeaders.name', { ns: 'common' })}</th>
                        <th>{t('password.tableHeaders.userName', { ns: 'common' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {selectedUsersPreview.map(user => (
                        <tr key={user.id}>
                            <td className='text-break'>{user.userDisplayName}</td>
                            <td className='text-break'>{user.username || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {remainingSelectedUsers > 0 &&
                <Button
                    color="link"
                    className="p-0 ml-3"
                    onClick={() => setShowAllSelectedUsers(true)}
                >
                    {t('changePasswords.modal.selectedUsersMore', {
                        ns: 'common',
                        count: remainingSelectedUsers
                    })}
                </Button>}

            {showAllSelectedUsers && selectedUsers.length > SELECTED_USERS_PREVIEW_LIMIT &&
                <Button
                    color="link"
                    className="p-0 ml-3"
                    onClick={() => setShowAllSelectedUsers(false)}
                >
                    {t('changePasswords.modal.selectedUsersShowLess', { ns: 'common' })}
                </Button>}
        </div>
    );
}

interface PasswordConfigurationPanelProps {
    selectedEnvironment?: string;
    onConfigurationChange: (config: { request: Omit<CorePasswordChangeRequest, 'trackingIds'>; validationError?: string }) => void;
    saveError?: string;
}

const PasswordConfigurationPanel: React.FC<PasswordConfigurationPanelProps> = ({
    selectedEnvironment,
    onConfigurationChange,
    saveError
}) => {
    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();
    const [appTokenId, setAppTokenId] = useState<string>('');
    const [passwordSourceMode, setPasswordSourceMode] = useState<PasswordSourceMode>(PasswordSourceMode.PROFILE);
    const [passwordProfileId, setPasswordProfileId] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [forcePasswordChangeAtNextLogin, setForcePasswordChangeAtNextLogin] = useState<boolean>(true);
    const appTokensState = useSelector((state: RootState) => state.passwordAppToken.apptoken);
    const passwordProfilesState = useSelector((state: RootState) => state.passwordProfile.passwordProfiles);

    const availablePasswordProfiles = useMemo(() => {
        if (passwordProfilesState.code === StatusCode.COMPLETE) {
            return passwordProfilesState.data;
        }

        if (passwordProfilesState.code === StatusCode.PENDING && passwordProfilesState.data) {
            return passwordProfilesState.data;
        }

        return [];
    }, [passwordProfilesState]);

    const tokenMatchesEnvironment = (token: CoreAppToken, environment?: string) => {
        if (environment === EnvironmentType.GOOGLE) {
            return token.appTokenType === AppTokenType.GOOGLE_SERVICE_ACCOUNT;
        }

        if (environment === EnvironmentType.ENTRA_ID) {
            return token.appTokenType === AppTokenType.MICROSOFT_APPLICATION || token.appTokenType === AppTokenType.MICROSOFT_DELEGATED;
        }

        return true;
    }

    const filteredAppTokens = useMemo(() => {
        if (appTokensState.code === StatusCode.COMPLETE) {
            return appTokensState.data.filter(token => tokenMatchesEnvironment(token, selectedEnvironment));
        }
        return [];
    }, [appTokensState, selectedEnvironment]);

    useEffect(() => {
        ListPasswordAppTokens()(dispatch as any);
        ListPasswordProfiles()(dispatch as any);
    }, [dispatch]);

    useEffect(() => {
        if (filteredAppTokens.length === 0) {
            if (appTokenId) {
                setAppTokenId('');
            }
            return;
        }

        if (!filteredAppTokens.some(token => token.id === appTokenId)) {
            setAppTokenId(filteredAppTokens[0].id);
        }
    }, [appTokenId, filteredAppTokens]);

    useEffect(() => {
        if (passwordSourceMode !== PasswordSourceMode.PROFILE) {
            return;
        }

        if (availablePasswordProfiles.length > 0 && !passwordProfileId) {
            setPasswordProfileId(availablePasswordProfiles[0].id);
            return;
        }

        if (availablePasswordProfiles.length === 0 && passwordProfilesState.code === StatusCode.COMPLETE && passwordProfileId) {
            setPasswordProfileId('');
        }
    }, [availablePasswordProfiles, passwordProfileId, passwordProfilesState.code, passwordSourceMode]);

    useEffect(() => {
        const request: Omit<CorePasswordChangeRequest, 'trackingIds'> = {
            appTokenId,
            password: passwordSourceMode === PasswordSourceMode.PASSWORD ? password : undefined,
            passwordProfileId: passwordSourceMode === PasswordSourceMode.PROFILE ? passwordProfileId : undefined,
            forcePasswordChangeAtNextLogin
        };

        const missingPassword = passwordSourceMode === PasswordSourceMode.PASSWORD && !password;
        const missingPasswordProfile = passwordSourceMode === PasswordSourceMode.PROFILE && !passwordProfileId;
        const isInvalid = !appTokenId || missingPassword || missingPasswordProfile;
        const validationError = isInvalid
            ? t('changePasswords.validation.missingFieldsForSource', {
                ns: 'common',
                source: passwordSourceMode === PasswordSourceMode.PASSWORD
                    ? t('changePasswords.modal.source.password', { ns: 'common' })
                    : t('changePasswords.modal.source.profile', { ns: 'common' })
            })
            : undefined;

        onConfigurationChange({ request, validationError });
    }, [
        appTokenId,
        password,
        passwordProfileId,
        passwordSourceMode,
        forcePasswordChangeAtNextLogin,
        onConfigurationChange,
        t
    ]);

    const appTokenDisplayName = (token: CoreAppToken) => {
        if (token.appTokenType === AppTokenType.GOOGLE_SERVICE_ACCOUNT) {
            return `Google (${token.userName})`;
        }
        else if (token.appTokenType === AppTokenType.MICROSOFT_APPLICATION) {
            return `EntraID (TenantId: ${token.tenant})`;
        }
        else if (token.appTokenType === AppTokenType.MICROSOFT_DELEGATED) {
            return `EntraID (Servicekonto: ${token.userName})`;
        }

        return token.appRegistration ?? token.id;
    }

    return (
        <>
            <FormGroup>
                <Label className="fw-bold" for="appTokenId">{t('changePasswords.modal.appTokenId', { ns: 'common' })}</Label>
                <Input
                    id="appTokenId"
                    type="select"
                    value={appTokenId}
                    onChange={(e) => setAppTokenId(e.target.value)}
                    disabled={appTokensState.code === StatusCode.PENDING || appTokensState.code === StatusCode.ERROR || filteredAppTokens.length === 0}
                >
                    {appTokensState.code !== StatusCode.COMPLETE && <option value="">{t('changePasswords.modal.appTokenIdPlaceholder', { ns: 'common' })}</option>}
                    {appTokensState.code === StatusCode.COMPLETE && filteredAppTokens.length === 0 && <option value="">{t('changePasswords.modal.noAppTokens', { ns: 'common' })}</option>}
                    {appTokensState.code === StatusCode.COMPLETE && filteredAppTokens.length > 0 && filteredAppTokens.map(token => (
                        <option key={token.id} value={token.id}>{appTokenDisplayName(token)}</option>
                    ))}
                </Input>
                {appTokensState.code === StatusCode.PENDING && <p>{t('changePasswords.modal.loadingAppTokens', { ns: 'common' })}</p>}
                {appTokensState.code === StatusCode.ERROR && <p className="red">{t('changePasswords.modal.appTokenLoadError', { ns: 'common' })}</p>}
                {appTokensState.code === StatusCode.COMPLETE && filteredAppTokens.length === 0 && <p className="red">{t('changePasswords.modal.noAppTokens', { ns: 'common' })}</p>}
            </FormGroup>

            <FormGroup>
                <div className="fw-bold mb-2">{t('changePasswords.modal.source.title', { ns: 'common' })}</div>
                <ButtonGroup className="d-flex w-100">
                    <Button
                        outline
                        onClick={() => setPasswordSourceMode(PasswordSourceMode.PROFILE)}
                        active={passwordSourceMode === PasswordSourceMode.PROFILE}
                    >
                        {t('changePasswords.modal.source.profile', { ns: 'common' })}
                    </Button>
                    <Button
                        outline
                        onClick={() => setPasswordSourceMode(PasswordSourceMode.PASSWORD)}
                        active={passwordSourceMode === PasswordSourceMode.PASSWORD}
                    >
                        {t('changePasswords.modal.source.password', { ns: 'common' })}
                    </Button>
                </ButtonGroup>
            </FormGroup>

            {passwordSourceMode === PasswordSourceMode.PASSWORD &&
                <FormGroup>
                    <Input
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('changePasswords.modal.passwordPlaceholder', { ns: 'common' })}
                    />
                </FormGroup>
            }

            {passwordSourceMode === PasswordSourceMode.PROFILE &&
                <FormGroup>
                    <Input
                        id="passwordProfileId"
                        type="select"
                        value={passwordProfileId}
                        onChange={(e) => setPasswordProfileId(e.target.value)}
                        disabled={passwordProfilesState.code === StatusCode.PENDING || passwordProfilesState.code === StatusCode.ERROR || availablePasswordProfiles.length === 0}
                    >
                        {passwordProfilesState.code !== StatusCode.COMPLETE && <option value="">{t('changePasswords.modal.passwordProfilePlaceholder', { ns: 'common' })}</option>}
                        {passwordProfilesState.code === StatusCode.COMPLETE && availablePasswordProfiles.length === 0 && <option value="">{t('changePasswords.modal.noPasswordProfiles', { ns: 'common' })}</option>}
                        {passwordProfilesState.code === StatusCode.COMPLETE && availablePasswordProfiles.length > 0 && availablePasswordProfiles.map(profile => (
                            <option key={profile.id} value={profile.id}>{profile.name}</option>
                        ))}
                    </Input>
                    {passwordProfilesState.code === StatusCode.PENDING && <p>{t('changePasswords.modal.loadingPasswordProfiles', { ns: 'common' })}</p>}
                    {passwordProfilesState.code === StatusCode.ERROR && <p className="red">{t('changePasswords.modal.passwordProfileLoadError', { ns: 'common' })}</p>}
                    {passwordProfilesState.code === StatusCode.COMPLETE && availablePasswordProfiles.length === 0 && <p className="red">{t('changePasswords.modal.noPasswordProfiles', { ns: 'common' })}</p>}
                </FormGroup>
            }

            <div className="fw-bold mt-4 mb-3">{t('changePasswords.modal.sectionForcePasswordChange', { ns: 'common' })}</div>

            <FormGroup check className='mb-3'>
                <Input
                    id="forcePasswordChangeAtNextLogin"
                    type="checkbox"
                    checked={forcePasswordChangeAtNextLogin}
                    onChange={(e) => setForcePasswordChangeAtNextLogin(e.target.checked)}
                />
                <Label check for="forcePasswordChangeAtNextLogin">
                    {t('changePasswords.modal.forcePasswordChangeAtNextLogin', { ns: 'common' })}
                </Label>
            </FormGroup>

            {saveError && <p className="red">{saveError}</p>}
        </>
    );
}

interface PasswordChangeResultsProps {
    changeResults: CorePasswordChangeResult[];
    selectedUsersById: Map<string, CorePasswordChangeUser>;
}

const PasswordChangeResults: React.FC<PasswordChangeResultsProps> = ({
    changeResults,
    selectedUsersById
}) => {
    const { t } = useTranslation(['common']);
    const successfulResults = changeResults.filter(x => x.success === true);
    const failedResults = changeResults.filter(x => x.success === false);
    const successfulChanges = successfulResults.length;
    const failedChanges = failedResults.length;
    const successfulCsvRows = successfulResults.map(result => {
        const user = result.tracking?.id ? selectedUsersById.get(result.tracking.id) : undefined;
        return {
            name: user?.userDisplayName ?? '',
            username: user?.username ?? '',
            newPassword: result.newPassword ?? ''
        };
    });

    const normalizedErrorMessage = (errorMessage?: string) => {
        const message = errorMessage?.trim();
        return message ? message : undefined;
    };

    const translatedErrorMessage = (errorMessage?: string) => {
        errorMessage = errorMessage?.toLocaleLowerCase();
        if (!errorMessage) {
            return t('changePasswords.modal.error.unknown', { ns: 'common' });
        }

        if (
            errorMessage.includes('password complexity')
        ) {
            return t('changePasswords.modal.error.complexity', { ns: 'common' });
        }

        if (
            errorMessage.includes('resource not found') ||
            errorMessage.includes('does not exist')
        ) {
            return t('changePasswords.modal.error.userNotFound', { ns: 'common' });
        }

        if (
            errorMessage.includes('forbidden') ||
            errorMessage.includes('unauthorized') ||
            errorMessage.includes('not authorized')
        ) {
            return t('changePasswords.modal.error.permissionDenied', { ns: 'common' });
        }

        return t('changePasswords.modal.error.unknown', { ns: 'common' });
    };

    return (
        <>
            <div className="mb-3">
                <Badge color="primary" className='me-2'>{t('changePasswords.modal.resultTotal', { ns: 'common' })}: {changeResults.length}</Badge>
                <Badge color="success" className='me-2'>{t('changePasswords.modal.resultSuccess', { ns: 'common' })}: {successfulChanges}</Badge>
                <Badge color="danger">{t('changePasswords.modal.resultFailed', { ns: 'common' })}: {failedChanges}</Badge>
            </div>

            {successfulResults.length > 0 &&
                <>
                    <h6>{t('changePasswords.modal.resultSuccess', { ns: 'common' })} ({successfulResults.length})</h6>
                    <CsvDownloadButton
                        rows={successfulCsvRows}
                        columns={[
                            { header: t('genericTableHeaders.name', { ns: 'common' }), value: row => row.name },
                            { header: t('password.tableHeaders.userName', { ns: 'common' }), value: row => row.username },
                            { header: t('changePasswords.modal.resultPassword', { ns: 'common' }), value: row => row.newPassword }
                        ]}
                        fileNamePrefix="password-change-success"
                        label={t('changePasswords.modal.downloadSuccessfulCsv', { ns: 'common' })}
                    />
                    <div className="mb-3">
                        <Table className="mb-0" striped>
                            <thead>
                                <tr>
                                    <th>{t('genericTableHeaders.name', { ns: 'common' })}</th>
                                    <th>{t('password.tableHeaders.userName', { ns: 'common' })}</th>
                                    <th>{t('changePasswords.modal.resultPassword', { ns: 'common' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {successfulResults.map((result, index) => {
                                    const user = result.tracking?.id ? selectedUsersById.get(result.tracking.id) : undefined;

                                    return (
                                        <tr key={`${result.tracking?.id ?? 'success'}-${index}`}>
                                            <td>{user?.userDisplayName ?? '-'}</td>
                                            <td>{user?.username ?? '-'}</td>
                                            <td>
                                                {result.newPassword
                                                    ? <div className="d-flex align-items-center">
                                                        <span>{result.newPassword}</span>
                                                        <Copy value={result.newPassword} />
                                                    </div>
                                                    : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </>
            }

            {failedResults.length > 0 &&
                <>
                    <h6>{t('changePasswords.modal.resultFailed', { ns: 'common' })} ({failedResults.length})</h6>
                    <div className="mb-3">
                        <Table className="mb-0" striped>
                            <thead>
                                <tr>
                                    <th>{t('genericTableHeaders.name', { ns: 'common' })}</th>
                                    <th>{t('password.tableHeaders.userName', { ns: 'common' })}</th>
                                    <th>{t('changePasswords.modal.resultError', { ns: 'common' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {failedResults.map((result, index) => {
                                    const user = result.tracking?.id ? selectedUsersById.get(result.tracking.id) : undefined;
                                    const rowKey = `${result.tracking?.id ?? 'failed'}-${index}`;
                                    const apiErrorMessage = normalizedErrorMessage(result.errorMessage);
                                    const tooltipTargetId = `password-change-error-info-${index}`;

                                    return (
                                        <tr key={rowKey}>
                                            <td>{user?.userDisplayName ?? '-'}</td>
                                            <td>{user?.username ?? '-'}</td>
                                            <td>
                                                <div className="d-flex align-items-start gap-2">
                                                    <span>{translatedErrorMessage(result.errorMessage)}</span>
                                                    {apiErrorMessage &&
                                                        <span
                                                            id={tooltipTargetId}
                                                            className="ms-1"
                                                            style={{ cursor: 'pointer' }}
                                                            aria-label={t('changePasswords.modal.error.showApiDetails', { ns: 'common' })}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon="question-circle"
                                                                className="explanation__icon"                                                                
                                                            />
                                                        </span>}
                                                </div>
                                                {apiErrorMessage &&
                                                    <UncontrolledTooltip
                                                        target={tooltipTargetId}
                                                        placement="top"
                                                        trigger="hover focus"
                                                        autohide={false}
                                                    >
                                                        <div >
                                                            <strong>{t('changePasswords.modal.error.apiDetails', { ns: 'common' })}</strong> {apiErrorMessage}
                                                        </div>
                                                    </UncontrolledTooltip>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </>
            }
        </>
    );
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = (props: PasswordChangeModalProps) => {
    const { t } = useTranslation(['common']);
    const selectedUsers = props.selectedUsers;

    const getTranslatedConfigurationError = (error: any) => {
        const title = error?.response?.data?.title as string | undefined;
        const detail = error?.response?.data?.detail as string | undefined;

        const errorMap: Record<string, string> = {
            InvalidPasswordSource: 'changePasswords.modal.error.invalidPasswordSource',
            UnsupportedAppTokenType: 'changePasswords.modal.error.unsupportedAppTokenType',
            WordListStorageUnavailable: 'changePasswords.modal.error.wordListStorageUnavailable',

            AppTokenMustBeSpecified: 'changePasswords.modal.error.appTokenMustBeSpecified',
            InvalidAppTokenId: 'changePasswords.modal.error.invalidAppTokenId',
            InvalidAppTokenType: 'changePasswords.modal.error.invalidAppTokenType',
            InvalidPasswordProfileId: 'changePasswords.modal.error.invalidPasswordProfileId',
            PasswordOrPasswordProfileIdMustBeSpecified: 'changePasswords.modal.error.passwordOrPasswordProfileIdMustBeSpecified',
            InvalidPasswordFormat: 'changePasswords.modal.error.invalidPasswordFormat'
        };

        const mappedKey = title && errorMap[title];
        if (mappedKey) {
            return t(mappedKey, { ns: 'common' });
        }

        return detail ?? t('error.couldNotFetch', { ns: 'common' });
    };

    const [passwordConfiguration, setPasswordConfiguration] = useState<{ request: Omit<CorePasswordChangeRequest, 'trackingIds'>; validationError?: string }>({
        request: {
            forcePasswordChangeAtNextLogin: true
        }
    });
    const [saveStatus, setSaveStatus] = useState<StatusCode>(StatusCode.NONE);
    const [saveError, setSaveError] = useState<string | undefined>(undefined);
    const [changeResults, setChangeResults] = useState<CorePasswordChangeResult[]>([]);

    useEffect(() => {
        if (props.isOpen) {
            setSaveStatus(StatusCode.NONE);
            setSaveError(undefined);
            setChangeResults([]);
        }
    }, [props.isOpen]);

    const submit = async () => {
        if (!selectedUsers.length || passwordConfiguration.validationError) {
            setSaveStatus(StatusCode.ERROR);
            setSaveError(passwordConfiguration.validationError ?? t('changePasswords.validation.missingFieldsForSource', {
                ns: 'common',
                source: t('changePasswords.modal.source.profile', { ns: 'common' })
            }));
            return;
        }

        try {
            setSaveStatus(StatusCode.PENDING);
            setSaveError(undefined);

            const response = await UpdateUserPasswords({
                ...passwordConfiguration.request,
                trackingIds: selectedUsers.map(x => x.id)
            });

            const results = response.data ?? [];
            setChangeResults(results);

            setSaveStatus(StatusCode.COMPLETE);
            props.onSuccess();
        }
        catch (error: any) {
            setSaveStatus(StatusCode.ERROR);
            setSaveError(getTranslatedConfigurationError(error));
        }
    }

    const close = () => {
        setSaveStatus(StatusCode.NONE);
        setSaveError(undefined);
        setChangeResults([]);
        props.onClose();
    }

    const hasFailedResult = changeResults.some(x => x.success === false);
    const hasResults = changeResults.length > 0;
    const selectedUsersById = useMemo(() => {
        return new Map(selectedUsers.map(user => [user.id, user]));
    }, [selectedUsers]);

    return (
        <ModalComponent
            isOpen={props.isOpen}
            header={t('changePasswords.modal.title', { ns: 'common' })}
            cancel={t('changePasswords.modal.close', { ns: 'common' })}
            confirm={hasResults ? undefined : t('changePasswords.modal.changePasswords', { ns: 'common' })}
            update={hasResults ? undefined : () => submit()}
            toggleModal={() => close()}
            disabled={!hasResults && saveStatus === StatusCode.PENDING}
            saveStatus={saveStatus}
            successMsg={hasFailedResult ? t('changePasswords.modal.successWithFailures', { ns: 'common' }) : t('changePasswords.modal.success', { ns: 'common' })}
            size='xl'
        >
            {!hasResults &&
                <Row>
                    <Col sm={6}>
                        <SelectedUsersPreview
                            selectedUsers={selectedUsers}
                        />
                    </Col>
                    <Col sm={6}>
                        <PasswordConfigurationPanel
                            selectedEnvironment={props.selectedEnvironment}
                            onConfigurationChange={setPasswordConfiguration}
                            saveError={saveError}
                        />
                    </Col>
                </Row>
            }

            {hasResults &&
                <PasswordChangeResults
                    changeResults={changeResults}
                    selectedUsersById={selectedUsersById}
                />
            }
        </ModalComponent>
    );
}

export default PasswordChangeModal;
